import { useEffect } from 'react';
import { Row, Col, Card } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import { styles } from './styles';
import { Link } from 'react-router-dom';
import { CATGORY_LINKS } from '../../utils/constant';
import { useMediaQuery } from '../../hooks/MediaQuery.hook';
import Seo from '../Seo';
import { PAGE_SEO, SITE_URL } from '../../utils/seo';
import logo from '../../assets/logo/logo.webp';

const gridStyle: React.CSSProperties = {
  width: '25%',
  textAlign: 'center',
};

interface categoryInterface {
  label: string;
  link: string;
}

export default function AboutUs() {
  const matches = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    document.title = PAGE_SEO.ABOUT.title;
  }, []);

  return (
    <Card title="About Koostream" style={matches ? { padding: '20px' } : {}}>
      <Seo
        title={PAGE_SEO.ABOUT.title}
        description={PAGE_SEO.ABOUT.description}
        canonical={`${SITE_URL}/about`}
      />
      <Row gutter={24} style={styles.logo}>
        <Col span={16} xs={24} sm={12} md={8} lg={6}>
          <img src={logo} className="logo-img" alt="Koostream logo" />
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={12} xs={24} sm={12}>
          <Card title="What is Koostream?" style={styles.card}>
            <Card.Meta title="Sports fixtures, schedules, standings, and original commentary" />
            <Paragraph style={styles.paragraph}>
              Koostream is an independent sports information site. We publish match fixtures, kickoff
              times, league standings, and original editorial analysis so fans can follow soccer,
              American football, basketball, combat sports, motorsport, cricket, and more in one
              place. We are not a licensed broadcaster and we do not host copyrighted game footage.
              Official live coverage is available only from leagues and their authorized media
              partners.
            </Paragraph>
          </Card>
        </Col>
        <Col span={12} xs={24} sm={12}>
          <Card title="What we publish" style={styles.card}>
            <Card.Meta title="Schedules, tables, and sports writing" />
            <Paragraph style={styles.paragraph}>
              Our pages list upcoming events by sport and competition, show points tables where
              available, and host original articles written for this site. Team names, league names,
              and logos belong to their owners and appear only to identify events and for news
              reporting. If you are a rights holder, please use our{' '}
              <Link to="/dmca">DMCA Policy</Link> to request a takedown.
            </Paragraph>
          </Card>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={12} xs={24} sm={12}>
          <Card title="How to use Koostream" style={styles.card}>
            <p style={styles.paragraph}>
              <strong className="steps">Step 1.</strong> Open the homepage or choose a sport from the
              navigation.
            </p>
            <p style={styles.paragraph}>
              <strong className="steps">Step 2.</strong> Browse today’s fixtures. Each listing shows
              the teams, competition, and scheduled time.
            </p>
            <p style={styles.paragraph}>
              <strong className="steps">Step 3.</strong> Open a match or league page for more detail,
              or visit <Link to="/leagues/standings">standings</Link> for points tables.
            </p>
            <p style={styles.paragraph}>
              <strong className="steps">Step 4.</strong> Read original commentary in the{' '}
              <Link to="/blogs">blog</Link>. For licensed video, use official league and network apps.
            </p>
          </Card>
        </Col>
        <Col span={12} xs={24} sm={12}>
          <Card title="Sports covered on Koostream" style={styles.card}>
            {CATGORY_LINKS?.map((cat: categoryInterface, index: number) => (
              <Card.Grid key={index} style={gridStyle}>
                <Link to={cat.link}>{cat.label}</Link>
              </Card.Grid>
            ))}
          </Card>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={12} xs={24} sm={12}>
          <Card title="Soccer fixtures and news" style={styles.card}>
            <Paragraph>
              Follow Premier League, Bundesliga, Serie A, Ligue 1, MLS, La Liga, and UEFA club
              competition schedules, plus World Cup and continental tournament fixtures when they are
              in season. Kickoff times and tables are for informational use. Broadcast rights remain
              with the competitions and their official partners.
            </Paragraph>
          </Card>
        </Col>
        <Col span={12} xs={24} sm={12}>
          <Card title="Other sports calendars" style={styles.card}>
            <Paragraph>
              Koostream also lists NFL, NBA, NHL, MLB, UFC and boxing cards, Formula 1, MotoGP,
              cricket, rugby, and tennis schedules. Event names identify the competition; they are
              not a claim of official partnership or of the right to exhibit live video.
            </Paragraph>
          </Card>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <Card title="Copyright and contact" style={styles.card}>
            <Paragraph style={styles.paragraph}>
              Koostream does not host copyrighted broadcasts. Third-party links, if shown, are not
              under our control and may be removed after a valid notice. Read our{' '}
              <Link to="/terms">Terms of Service</Link>, <Link to="/privacy-policy">Privacy Policy</Link>
              , and <Link to="/dmca">DMCA Policy</Link>, or <Link to="/contact-us">contact us</Link>.
            </Paragraph>
          </Card>
        </Col>
      </Row>
    </Card>
  );
}
